import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './AboutUsPage.css';
import Footer from '../../components/Footer/Footer';
import UnifiedNavigation from '../../components/Navigation/UnifiedNavigation';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import { HandDrawnButton } from '../../components/shared/HandDrawnButton/HandDrawnButton';
import { CTALink } from '../../components/shared/CTALink/CTALink';
import { initializeDragScroll } from '../../utils/interactiveFeatures';
import { createSelfbookHandler } from '../../utils/selfbook';

const AboutUsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleBooking = createSelfbookHandler(navigate, '/contact');

  useEffect(() => {
    // Initialize drag scroll for all carousels
    const cleanupFns = [
      initializeDragScroll('#draggable-carousel'),
      initializeDragScroll('#people-carousel'),
      initializeDragScroll('#destination-carousel')
    ];

    // Cleanup functions
    return () => {
      cleanupFns.forEach(cleanup => cleanup && cleanup());
    };
  }, []);
  return (
    <div className="about-us-page">
      {/* Navigation Header */}
      <UnifiedNavigation variant="light" />

      {/* Diptych Carousel Section */}
      <div className="about-diptych-section">
        <div className="diptych-carousel" id="draggable-carousel">
          <div className="diptych-slide">
            <div className="diptych-left">
              <div className="diptych-content">
                <h2>{t('pages/aboutUs:intro.title')}</h2>
                <h3>{t('pages/aboutUs:intro.howItStarted.title')}</h3>
                <p>{t('pages/aboutUs:intro.howItStarted.text')}</p>
              </div>
            </div>
            <div className="diptych-right diptych-image-1"></div>
          </div>

          <div className="diptych-slide">
            <div className="diptych-left">
              <div className="diptych-content">
                <p>{t('pages/aboutUs:intro.history.nameOrigin')}</p>
                <p>{t('pages/aboutUs:intro.history.design')}</p>
              </div>
            </div>
            <div className="diptych-right diptych-image-2"></div>
          </div>

          <div className="diptych-slide">
            <div className="diptych-left">
              <div className="diptych-content">
                <h3>{t('pages/aboutUs:intro.newDestinations.title')}</h3>
                <p>{t('pages/aboutUs:intro.newDestinations.text')}</p>
              </div>
            </div>
            <div className="diptych-right diptych-image-3"></div>
          </div>
        </div>
      </div>

      {/* Map Block */}
      <div className="about-map-section">
        <div className="map-content">
          <h3>{t('pages/aboutUs:map.title')}</h3>
          <p>{t('pages/aboutUs:map.text')}</p>
        </div>
        <div className="map-container">
          <div className="map-image"></div>
          <div className="map-legend">
            <h3>{t('pages/aboutUs:map.ourLocations')}</h3>
            <div className="map-location-item">
              <span className="location-dot atacama"></span>
              <span>{t('pages/aboutUs:map.locations.atacama')}</span>
            </div>
            <div className="map-location-item">
              <span className="location-dot patagonia"></span>
              <span>{t('pages/aboutUs:map.locations.patagonia')}</span>
            </div>
            <div className="map-location-item">
              <span className="location-dot iguazu"></span>
              <span>{t('pages/aboutUs:map.locations.iguazu')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Text Block + Button */}
      <div className="about-text-block">
        <div className="text-block-content">
          <h2>{t('pages/aboutUs:exploration.title')}</h2>
          <p>{t('pages/aboutUs:exploration.text')}</p>
          <HandDrawnButton
            text={t('pages/aboutUs:exploration.button')}
            variant="brand"
            color="awasi"
            animation="stroke-to-fill"
            onClick={() => navigate('/destinations/')}
            className="about-cta-button"
          />
        </div>
      </div>

      {/* People Carousel */}
      <div className="about-people-section">
        <div className="people-header">
          <h2>{t('pages/aboutUs:team.title')}</h2>
          <p>{t('pages/aboutUs:team.text')}</p>
          <HandDrawnButton
            text={t('pages/aboutUs:team.button')}
            variant="brand"
            color="awasi"
            animation="stroke-to-fill"
            onClick={() => navigate('/careers/')}
            className="join-team-button"
          />
        </div>

        <div className="people-carousel" id="people-carousel">
          <div className="people-slide">
            <div className="person-image person-1"></div>
            <h3>{t('pages/aboutUs:team.members.marcoSalinas.name')}</h3>
            <p className="person-title">{t('pages/aboutUs:team.members.marcoSalinas.position')}</p>
            <p className="person-bio">{t('pages/aboutUs:team.members.marcoSalinas.bio')}</p>
            <Link to="/our-story/" className="read-more-link">{t('pages/aboutUs:team.readMore')}</Link>
          </div>

          <div className="people-slide">
            <div className="person-image person-2"></div>
            <h3>{t('pages/aboutUs:team.members.paulaBertotto.name')}</h3>
            <p className="person-title">{t('pages/aboutUs:team.members.paulaBertotto.position')}</p>
            <p className="person-bio">{t('pages/aboutUs:team.members.paulaBertotto.bio')}</p>
            <Link to="/our-story/" className="read-more-link">{t('pages/aboutUs:team.readMore')}</Link>
          </div>

          <div className="people-slide">
            <div className="person-image person-3"></div>
            <h3>{t('pages/aboutUs:team.members.juanIgnacio.name')}</h3>
            <p className="person-title">{t('pages/aboutUs:team.members.juanIgnacio.position')}</p>
            <p className="person-bio">{t('pages/aboutUs:team.members.juanIgnacio.bio')}</p>
            <Link to="/our-story/" className="read-more-link">{t('pages/aboutUs:team.readMore')}</Link>
          </div>

          <div className="people-slide">
            <div className="person-image person-4"></div>
            <h3>{t('pages/aboutUs:team.members.florenciaLavagno.name')}</h3>
            <p className="person-title">{t('pages/aboutUs:team.members.florenciaLavagno.position')}</p>
            <p className="person-bio">{t('pages/aboutUs:team.members.florenciaLavagno.bio')}</p>
            <Link to="/our-story/" className="read-more-link">{t('pages/aboutUs:team.readMore')}</Link>
          </div>

          <div className="people-slide">
            <div className="person-image person-5"></div>
            <h3>Magna gravida augue</h3>
            <p className="person-title">AWASI - POSITION</p>
            <p className="person-bio">Placeholder text for team member bio.</p>
            <Link to="/our-story/" className="read-more-link">READ MORE</Link>
          </div>
        </div>
      </div>

      {/* Header + Image with Details */}
      <div className="about-details-section">
        <div className="details-header">
          <h2>{t('pages/aboutUs:equality.title')}</h2>
        </div>
        <div className="details-content">
          <div className="details-image"></div>
          <div className="details-text">
            <div className="equality-card">
              <h3>{t('pages/aboutUs:equality.initiatives.inclusion.title')}</h3>
              <p>{t('pages/aboutUs:equality.initiatives.inclusion.text')}</p>
              <Link to="/our-story/" className="read-more-link">{t('pages/aboutUs:equality.readMore')}</Link>
            </div>
            <div className="equality-card">
              <h3>{t('pages/aboutUs:equality.initiatives.internship.title')}</h3>
              <p>{t('pages/aboutUs:equality.initiatives.internship.text')}</p>
              <Link to="/our-story/" className="read-more-link">{t('pages/aboutUs:equality.readMore')}</Link>
            </div>
            <div className="equality-card">
              <h3>{t('pages/aboutUs:equality.initiatives.education.title')}</h3>
              <p>{t('pages/aboutUs:equality.initiatives.education.text')}</p>
              <Link to="/our-story/" className="read-more-link">{t('pages/aboutUs:equality.readMore')}</Link>
            </div>
            <div className="equality-card">
              <h3>{t('pages/aboutUs:equality.initiatives.guarani.title')}</h3>
              <p>{t('pages/aboutUs:equality.initiatives.guarani.text')}</p>
              <Link to="/our-story/" className="read-more-link">{t('pages/aboutUs:equality.readMore')}</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Destinations Section */}
      <div className="about-destinations-section">
        <h2>{t('pages/aboutUs:destinations.title')}</h2>
        <div className="destinations-grid">
          <a href="/atacama" className="destination-card">
            <div className="destination-image dest-atacama"></div>
            <h3>{t('pages/aboutUs:destinations.locations.atacama.name')}</h3>
            <CTALink
              text={t('pages/aboutUs:destinations.visitProperty')}
              type="secondary"
              color="awasi"
              className="visit-property-btn"
            />
            <CTALink
              text={t('pages/aboutUs:destinations.bookStay')}
              type="primary"
              color="awasi"
              onClick={handleBooking}
              className="book-stay-btn"
            />
            <span className="location-tag">{t('pages/aboutUs:destinations.locations.atacama.region')}</span>
          </a>

          <a href="/patagonia" className="destination-card">
            <div className="destination-image dest-patagonia"></div>
            <h3>{t('pages/aboutUs:destinations.locations.patagonia.name')}</h3>
            <CTALink
              text={t('pages/aboutUs:destinations.visitProperty')}
              type="secondary"
              color="awasi"
              className="visit-property-btn"
            />
            <CTALink
              text={t('pages/aboutUs:destinations.bookStay')}
              type="primary"
              color="awasi"
              onClick={handleBooking}
              className="book-stay-btn"
            />
            <span className="location-tag">{t('pages/aboutUs:destinations.locations.patagonia.region')}</span>
          </a>

          <a href="/iguazu" className="destination-card">
            <div className="destination-image dest-iguazu"></div>
            <h3>Iguazú</h3>
            <CTALink
              text="VISIT THE PROPERTY"
              type="secondary"
              color="awasi"
              className="visit-property-btn"
            />
            <CTALink
              text="BOOK YOUR STAY"
              type="primary"
              color="awasi"
              onClick={handleBooking}
              className="book-stay-btn"
            />
            <span className="location-tag">NORTHERN ARGENTINA</span>
          </a>
        </div>
      </div>

      {/* Destination Carousel */}
      <div className="about-destination-carousel-section">
        <div className="destination-carousel" id="destination-carousel">
          <div className="destination-slide">
            <div className="destination-carousel-image dest-carousel-1"></div>
            <div className="destination-info">
              <h3>Awasi Atacama</h3>
              <p>12 Suites • San Pedro de Atacama</p>
            </div>
          </div>

          <div className="destination-slide">
            <div className="destination-carousel-image dest-carousel-2"></div>
            <div className="destination-info">
              <h3>Awasi Patagonia</h3>
              <p>14 Villas • Torres del Paine</p>
            </div>
          </div>

          <div className="destination-slide">
            <div className="destination-carousel-image dest-carousel-3"></div>
            <div className="destination-info">
              <h3>Awasi Iguazú</h3>
              <p>14 Villas + 1 Master • Misiones</p>
            </div>
          </div>

          <div className="destination-slide">
            <div className="destination-carousel-image dest-carousel-4"></div>
            <div className="destination-info">
              <h3>Awasi Mendoza</h3>
              <p>17 Villas • Western Argentina</p>
            </div>
          </div>

          <div className="destination-slide">
            <div className="destination-carousel-image dest-carousel-5"></div>
            <div className="destination-info">
              <h3>Awasi Santa Catarina</h3>
              <p>25 Villas • Southern Brazil</p>
            </div>
          </div>
        </div>
      </div>

      {/* Press & Awards Section */}
      <div className="about-press-awards-section">
        <div className="press-awards-header">
          <h2>{t('common:pressAwards')}</h2>
        </div>
        <div className="awards-grid">
          <div className="award-item">
            <h3>The Telegraph The world's 50 greatest hotels.</h3>
            <p>AWASI - AWASI PATAGONIA</p>
          </div>
          <div className="award-item">
            <h3>Conde Nast Traveller Gold List 2019</h3>
            <p>AWASI - AWASI IGUAZÚ</p>
          </div>
          <div className="award-item">
            <h3>Travel + Leisure The Top 100 Hotels in the World 2020</h3>
            <p>AWASI - AWASI ATACAMA</p>
          </div>
          <div className="award-item">
            <h3>Hollywood's International Documentary Festival</h3>
            <p>AWASI - AWASI IGUAZÚ</p>
          </div>
          <div className="award-item">
            <h3>The Tripadvisor 2024 Travelers' Choice Awards - Best of the Best</h3>
            <p>AWASI - AWASI SANTA CATARINA</p>
          </div>
          <div className="award-item">
            <h3>Wine Spectator 2025 Award of Excellence</h3>
            <p>AWASI - AWASI MENDOZA</p>
          </div>
        </div>
        <HandDrawnButton
          text={t('pages/ourStory:pressAwards.seeMore')}
          variant="brand"
          color="awasi"
          animation="stroke-to-fill"
          onClick={() => navigate('/press-awards/')}
          className="see-more-btn"
        />
      </div>

      {/* Instagram Feed + Footer */}
      <InstagramSection />
      <Footer variant="atacama" />
    </div>
  );
};

export default AboutUsPage;